import {
  userErrorMessage,
  assignmentsResponseSchema,
  homeworkAnswerPreviewResponseSchema,
  homeworkAnswerSessionResponseSchema,
  type HomeworkAnswerSessionBody,
  type HomeworkAnswerSessionResponse,
  homeworkAssignmentResponseSchema,
  homeworkReviewProgressResponseSchema,
  homeworkResponseSchema,
  homeworkSubmissionReplayResponseSchema,
  homeworksResponseSchema,
  submissionResponseSchema,
  type HomeworkAnswerMode,
  type HomeworkAnswerTraceNode,
  type HomeworkAnswerTrace,
  type HomeworkAnswerPreviewResponse,
  type HomeworkAssignmentSnapshot,
  type HomeworkReviewProgressResponse,
  type HomeworkSnapshot,
  type HomeworkSubmissionReplayResponse,
  type HomeworkTimeLimitSeconds,
  type BoardSnapshot,
  type ProblemMoveIntent,
  type SubmissionSnapshot,
  type SubmissionStatus,
} from '@wx-weiqi/contracts';

import { nextRequestId, WebApiError } from '../services/http';
import { canAnswerHomeworkProblem } from '../domain/homework-answer-progress';
import { latestHomeworkSubmissionForProblem } from '../domain/homework-grading';
import {
  studentHomeLearningSummary,
  type StudentHomeLearningSummary,
} from '../domain/student-home';
import type { IdentityStore } from './identity-store';

const MAX_HOMEWORK_PAGES = 100;
const HOMEWORK_OVERVIEW_CONCURRENCY = 6;
const PUBLISH_NOTICE_DURATION_MS = 5_000;

async function mapWithConcurrency<T, Result>(
  values: readonly T[],
  concurrency: number,
  task: (value: T) => Promise<Result>,
): Promise<readonly Result[]> {
  const results: Array<Result | undefined> = Array.from({ length: values.length }, () => undefined);
  let cursor = 0;
  const worker = async () => {
    while (cursor < values.length) {
      const index = cursor;
      cursor += 1;
      const value = values[index];
      if (value === undefined) return;
      results[index] = await task(value);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(Math.max(1, concurrency), values.length) }, worker),
  );
  return results.flatMap((result) => (result === undefined ? [] : [result]));
}

export interface HomeworkState {
  readonly homeworks: readonly HomeworkSnapshot[];
  readonly assignments: readonly HomeworkAssignmentSnapshot[];
  readonly reviewAssignmentsByHomework: Readonly<
    Record<string, readonly HomeworkAssignmentSnapshot[]>
  >;
  readonly reviewProgressByHomework: Readonly<
    Record<string, HomeworkReviewProgressResponse['data']>
  >;
  readonly selectedAssignment: HomeworkAssignmentSnapshot | null;
  readonly preview: HomeworkAnswerPreviewResponse['data'] | null;
  readonly pending: string | null;
  readonly error: string | null;
  readonly notice: string | null;
}

export interface CreateHomeworkInput {
  readonly classId: string;
  readonly title: string;
  readonly questionBankProblemIds: readonly string[];
  readonly timeLimitSeconds: HomeworkTimeLimitSeconds;
  readonly maxAttempts: 1 | 2 | 3;
  readonly answerMode: HomeworkAnswerMode;
  readonly studentMembershipIds: readonly string[];
}

export interface HomeworkAnswerFrame {
  readonly answer: readonly ProblemMoveIntent[];
  readonly board: BoardSnapshot;
  readonly solutionComplete: boolean;
  readonly traceNodeIndex: number;
}

export interface HomeworkAnswerDraft {
  readonly assignmentId: string;
  readonly problemIndex: number;
  readonly frames: readonly HomeworkAnswerFrame[];
  readonly traceNodes: readonly HomeworkAnswerTraceNode[];
  readonly frameIndex: number;
  readonly elapsedMs: number;
}

type Listener = () => void;

export class HomeworkStore {
  private stateValue: HomeworkState = {
    homeworks: [],
    assignments: [],
    reviewAssignmentsByHomework: {},
    reviewProgressByHomework: {},
    selectedAssignment: null,
    preview: null,
    pending: null,
    error: null,
    notice: null,
  };
  private readonly listeners = new Set<Listener>();
  private readonly answerDrafts = new Map<string, HomeworkAnswerDraft>();
  private readonly submissionRequestIds = new Map<string, string>();
  private lifecycleEpoch = 0;
  private reviewDataRequestEpoch = 0;
  private researchRequestEpoch = 0;
  private replayRequestEpoch = 0;
  private researchErrorMessage: string | null = null;
  private replayErrorMessage: string | null = null;
  private publishNoticeTimer: ReturnType<typeof setTimeout> | null = null;
  private studentLearningValue: StudentHomeLearningSummary = { status: 'loading' };
  private studentLearningRequestEpoch = 0;

  constructor(private readonly identity: IdentityStore) {}

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): HomeworkState => this.stateValue;

  readonly studentLearningSnapshot = (): StudentHomeLearningSummary => this.studentLearningValue;

  clearMessages(): void {
    this.researchErrorMessage = null;
    this.replayErrorMessage = null;
    this.update({ ...this.stateValue, error: null, notice: null });
  }

  dismissNotice(): void {
    if (this.stateValue.notice !== null) this.update({ ...this.stateValue, notice: null });
  }

  clear(): void {
    this.lifecycleEpoch += 1;
    this.studentLearningRequestEpoch += 1;
    this.studentLearningValue = { status: 'loading' };
    this.reviewDataRequestEpoch += 1;
    this.researchRequestEpoch += 1;
    this.replayRequestEpoch += 1;
    this.researchErrorMessage = null;
    this.replayErrorMessage = null;
    this.answerDrafts.clear();
    this.submissionRequestIds.clear();
    this.update({
      homeworks: [],
      assignments: [],
      reviewAssignmentsByHomework: {},
      reviewProgressByHomework: {},
      selectedAssignment: null,
      preview: null,
      pending: null,
      error: null,
      notice: null,
    });
  }

  clearSelection(): void {
    this.update({ ...this.stateValue, selectedAssignment: null, preview: null });
  }

  answerDraft(assignmentId: string): HomeworkAnswerDraft | null {
    const draft = this.answerDrafts.get(assignmentId) ?? null;
    const assignment = this.stateValue.selectedAssignment;
    if (draft !== null && assignment?.assignmentId === assignmentId) {
      const problemId = assignment.items[draft.problemIndex]?.problem.problemId;
      if (problemId === undefined || !canAnswerHomeworkProblem(assignment, problemId)) {
        this.clearAnswerDraft(assignmentId);
        return null;
      }
    }
    return draft;
  }

  saveAnswerDraft(draft: HomeworkAnswerDraft): void {
    this.answerDrafts.set(draft.assignmentId, {
      ...draft,
      frames: [...draft.frames],
      traceNodes: [...draft.traceNodes],
    });
  }

  clearAnswerDraft(assignmentId: string): void {
    this.answerDrafts.delete(assignmentId);
  }

  async createAndPublish(input: CreateHomeworkInput): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('create-homework');
    try {
      const created = await this.identity.authorized(
        `/classes/${encodeURIComponent(input.classId)}/homeworks`,
        homeworkResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-homework-create'),
          body: {
            title: input.title.trim(),
            description: null,
            dueAt: null,
            maxAttempts: input.answerMode === 'MANUAL' ? 1 : input.maxAttempts,
            answerMode: input.answerMode,
            timeLimitSeconds: input.timeLimitSeconds,
            questionBankProblemIds: [...input.questionBankProblemIds],
          },
        },
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return;
      const published = await this.identity.authorized(
        `/homeworks/${encodeURIComponent(created.data.homework.homeworkId)}/publish`,
        homeworkResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-homework-publish'),
          body: {
            expectedHomeworkVersion: created.data.homework.homeworkVersion,
            studentMembershipIds: [...input.studentMembershipIds],
          },
        },
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return;
      this.update({
        ...this.stateValue,
        homeworks: [
          published.data.homework,
          ...this.stateValue.homeworks.filter(
            (homework) => homework.homeworkId !== published.data.homework.homeworkId,
          ),
        ],
        pending: null,
        error: null,
        notice: `“${published.data.homework.title}”已发送给 ${input.studentMembershipIds.length} 名学生。`,
      });
      this.publishNoticeTimer = setTimeout(() => {
        this.publishNoticeTimer = null;
        this.dismissNotice();
      }, PUBLISH_NOTICE_DURATION_MS);
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) {
        this.fail(error, '作业布置失败，请检查题目、班级与学生后重试。');
      }
      throw error;
    }
  }

  async loadClassHomeworks(classId: string): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('load-homeworks');
    try {
      const response = await this.identity.authorized(
        `/classes/${encodeURIComponent(classId)}/homeworks?limit=50`,
        homeworksResponseSchema,
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return;
      this.update({
        ...this.stateValue,
        homeworks: response.data.homeworks,
        assignments: [],
        reviewAssignmentsByHomework: {},
        reviewProgressByHomework: {},
        selectedAssignment: null,
        preview: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '作业列表加载失败。');
      throw error;
    }
  }

  async deleteHomework(homework: HomeworkSnapshot): Promise<void> {
    const epoch = this.lifecycleEpoch;
    this.reviewDataRequestEpoch += 1;
    this.pending('delete-homework');
    try {
      await this.identity.authorized(
        `/homeworks/${encodeURIComponent(homework.homeworkId)}`,
        homeworkResponseSchema,
        {
          method: 'DELETE',
          requestId: nextRequestId('web-homework-delete'),
          body: { expectedHomeworkVersion: homework.homeworkVersion },
        },
      );
      if (epoch !== this.lifecycleEpoch) return;
      this.reviewDataRequestEpoch += 1;
      const reviewAssignments = { ...this.stateValue.reviewAssignmentsByHomework };
      const reviewProgress = { ...this.stateValue.reviewProgressByHomework };
      delete reviewAssignments[homework.homeworkId];
      delete reviewProgress[homework.homeworkId];
      const selectedDeleted =
        this.stateValue.selectedAssignment?.homeworkId === homework.homeworkId;
      for (const assignment of this.stateValue.assignments) {
        if (assignment.homeworkId === homework.homeworkId)
          this.clearAnswerDraft(assignment.assignmentId);
      }
      this.update({
        ...this.stateValue,
        homeworks: this.stateValue.homeworks.filter(
          (item) => item.homeworkId !== homework.homeworkId,
        ),
        assignments: this.stateValue.assignments.filter(
          (item) => item.homeworkId !== homework.homeworkId,
        ),
        reviewAssignmentsByHomework: reviewAssignments,
        reviewProgressByHomework: reviewProgress,
        selectedAssignment: selectedDeleted ? null : this.stateValue.selectedAssignment,
        preview: selectedDeleted ? null : this.stateValue.preview,
        pending: null,
        error: null,
        notice: '作业已删除，学生端不再显示。',
      });
    } catch (error: unknown) {
      if (epoch === this.lifecycleEpoch) this.fail(error, '作业删除失败，请刷新后重试。');
      throw error;
    }
  }

  async loadTeacherHomeworkOverview(classIds: readonly string[]): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    const requestEpoch = ++this.reviewDataRequestEpoch;
    this.pending('load-homework-review-overview');
    try {
      const homeworkPages = await mapWithConcurrency(
        [...new Set(classIds)],
        HOMEWORK_OVERVIEW_CONCURRENCY,
        (classId) => this.loadAllClassHomeworks(classId, lifecycleEpoch),
      );
      if (lifecycleEpoch !== this.lifecycleEpoch || requestEpoch !== this.reviewDataRequestEpoch) {
        return;
      }
      const homeworks = homeworkPages
        .flat()
        .filter((homework) => homework.status !== 'DRAFT')
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      const progressEntries = await mapWithConcurrency(
        homeworks,
        HOMEWORK_OVERVIEW_CONCURRENCY,
        async (homework) => {
          const response = await this.identity.authorized(
            `/homeworks/${encodeURIComponent(homework.homeworkId)}/review-progress`,
            homeworkReviewProgressResponseSchema,
          );
          return [homework.homeworkId, response.data] as const;
        },
      );
      if (lifecycleEpoch !== this.lifecycleEpoch || requestEpoch !== this.reviewDataRequestEpoch) {
        return;
      }
      this.update({
        ...this.stateValue,
        homeworks,
        assignments: [],
        reviewAssignmentsByHomework: {},
        reviewProgressByHomework: Object.fromEntries(progressEntries),
        selectedAssignment: null,
        preview: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch && requestEpoch === this.reviewDataRequestEpoch) {
        this.fail(error, '批改作业列表加载失败。');
      }
      throw error;
    }
  }

  async loadTeacherHomeworkDataset(classIds: readonly string[]): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    const requestEpoch = ++this.reviewDataRequestEpoch;
    this.pending('load-homework-data');
    try {
      const homeworkPages = await mapWithConcurrency(
        [...new Set(classIds)],
        HOMEWORK_OVERVIEW_CONCURRENCY,
        (classId) => this.loadAllClassHomeworks(classId, lifecycleEpoch),
      );
      if (lifecycleEpoch !== this.lifecycleEpoch || requestEpoch !== this.reviewDataRequestEpoch) {
        return;
      }
      const homeworks = homeworkPages
        .flat()
        .filter((homework) => homework.status !== 'DRAFT')
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
      const datasetEntries = await mapWithConcurrency(
        homeworks,
        HOMEWORK_OVERVIEW_CONCURRENCY,
        async (homework) => {
          const [assignments, progress] = await Promise.all([
            this.loadAllAssignmentsForHomework(homework.homeworkId, lifecycleEpoch),
            this.identity.authorized(
              `/homeworks/${encodeURIComponent(homework.homeworkId)}/review-progress`,
              homeworkReviewProgressResponseSchema,
            ),
          ]);
          return [homework.homeworkId, assignments, progress.data] as const;
        },
      );
      if (lifecycleEpoch !== this.lifecycleEpoch || requestEpoch !== this.reviewDataRequestEpoch) {
        return;
      }
      this.update({
        ...this.stateValue,
        homeworks,
        assignments: [],
        reviewAssignmentsByHomework: Object.fromEntries(
          datasetEntries.map(([homeworkId, assignments]) => [homeworkId, assignments]),
        ),
        reviewProgressByHomework: Object.fromEntries(
          datasetEntries.map(([homeworkId, , progress]) => [homeworkId, progress]),
        ),
        selectedAssignment: null,
        preview: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch && requestEpoch === this.reviewDataRequestEpoch) {
        this.fail(error, '作业数据加载失败。');
      }
      throw error;
    }
  }

  async loadHomeworkAssignments(homeworkId: string): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    const requestEpoch = ++this.reviewDataRequestEpoch;
    this.pending('load-submissions');
    try {
      const assignments = await this.loadAllAssignmentsForHomework(homeworkId, lifecycleEpoch);
      if (lifecycleEpoch !== this.lifecycleEpoch || requestEpoch !== this.reviewDataRequestEpoch) {
        return;
      }
      this.update({
        ...this.stateValue,
        assignments,
        reviewAssignmentsByHomework: {
          ...this.stateValue.reviewAssignmentsByHomework,
          [homeworkId]: assignments,
        },
        selectedAssignment: null,
        preview: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch && requestEpoch === this.reviewDataRequestEpoch) {
        this.fail(error, '学生作答加载失败。');
      }
      throw error;
    }
  }

  /** Isolated from answer drafts, timer sessions and teacher grading state. */
  async researchAssignmentProblem(
    assignmentId: string,
    problemId: string,
    answer: readonly ProblemMoveIntent[],
  ): Promise<HomeworkAnswerPreviewResponse['data']> {
    const response = await this.identity.authorized(
      `/homework-assignments/${encodeURIComponent(assignmentId)}/research`,
      homeworkAnswerPreviewResponseSchema,
      { method: 'POST', body: { problemId, answer: [...answer] } },
    );
    return response.data;
  }

  async researchHomeworkProblem(
    homeworkId: string,
    problemId: string,
    answer: readonly ProblemMoveIntent[],
  ): Promise<HomeworkAnswerPreviewResponse['data']> {
    const lifecycleEpoch = this.lifecycleEpoch;
    const requestEpoch = ++this.researchRequestEpoch;
    this.researchErrorMessage = null;
    this.update({ ...this.stateValue, error: null });
    try {
      const response = await this.identity.authorized(
        `/homeworks/${encodeURIComponent(homeworkId)}/research`,
        homeworkAnswerPreviewResponseSchema,
        {
          method: 'POST',
          body: { problemId, answer: [...answer] },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch && requestEpoch === this.researchRequestEpoch) {
        const message = userErrorMessage(error, '研究变化无法落子。');
        this.researchErrorMessage = message;
        this.update({
          ...this.stateValue,
          error: message,
        });
      }
      throw error;
    }
  }

  cancelHomeworkResearch(): void {
    this.researchRequestEpoch += 1;
    const shouldClearError =
      this.researchErrorMessage !== null && this.stateValue.error === this.researchErrorMessage;
    this.researchErrorMessage = null;
    if (shouldClearError) {
      this.update({ ...this.stateValue, error: null });
    }
  }

  /** Home reads have separate state and never reset an open answer or its draft. */
  async loadStudentLearning(): Promise<void> {
    const scope = this.studentLearningScope();
    if (scope === null) return;
    const lifecycleEpoch = this.lifecycleEpoch;
    const requestEpoch = ++this.studentLearningRequestEpoch;
    const isCurrent = () =>
      lifecycleEpoch === this.lifecycleEpoch &&
      requestEpoch === this.studentLearningRequestEpoch &&
      scope === this.studentLearningScope();
    this.updateStudentLearning({ status: 'loading' });
    try {
      const response = await this.identity.authorized(
        '/me/homework-assignments?limit=50',
        assignmentsResponseSchema,
      );
      if (!isCurrent()) return;
      this.updateStudentLearning(
        studentHomeLearningSummary(response.data.assignments, response.serverTimestamp),
      );
    } catch (error: unknown) {
      if (isCurrent()) {
        this.updateStudentLearning({ status: 'error', message: '学习进度暂时无法加载，请重试。' });
      }
      throw error;
    }
  }

  async loadMyAssignments(): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('load-my-homework');
    try {
      const response = await this.identity.authorized(
        '/me/homework-assignments?limit=50',
        assignmentsResponseSchema,
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return;
      this.update({
        ...this.stateValue,
        assignments: response.data.assignments,
        selectedAssignment: null,
        preview: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '我的作业加载失败。');
      throw error;
    }
  }

  async selectAssignment(assignmentId: string): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('open-assignment');
    try {
      const response = await this.identity.authorized(
        `/homework-assignments/${encodeURIComponent(assignmentId)}`,
        homeworkAssignmentResponseSchema,
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return;
      this.update({
        ...this.stateValue,
        selectedAssignment: response.data.assignment,
        preview: null,
        pending: null,
        error: null,
      });
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '作业打开失败。');
      throw error;
    }
  }

  async refreshSelectedAssignment(): Promise<void> {
    const assignmentId = this.stateValue.selectedAssignment?.assignmentId;
    if (!assignmentId) return;
    const epoch = this.lifecycleEpoch;
    try {
      const response = await this.identity.authorized(
        `/homework-assignments/${encodeURIComponent(assignmentId)}`,
        homeworkAssignmentResponseSchema,
      );
      if (
        epoch !== this.lifecycleEpoch ||
        this.stateValue.selectedAssignment?.assignmentId !== assignmentId
      )
        return;
      // Refresh only presentation text. In-flight answers and local navigation remain untouched.
      const requirements = new Map(
        response.data.assignment.items.map((item) => [
          item.problem.problemId,
          {
            requirements: item.problem.requirements ?? null,
            requirementsRichText: item.problem.requirementsRichText ?? null,
          },
        ]),
      );
      this.update({
        ...this.stateValue,
        selectedAssignment: {
          ...this.stateValue.selectedAssignment,
          items: this.stateValue.selectedAssignment.items.map((item) => ({
            ...item,
            problem: {
              ...item.problem,
              requirements: requirements.get(item.problem.problemId)?.requirements ?? null,
              requirementsRichText:
                requirements.get(item.problem.problemId)?.requirementsRichText ?? null,
            },
          })),
        },
      });
    } catch (error: unknown) {
      if (
        epoch === this.lifecycleEpoch &&
        this.stateValue.selectedAssignment?.assignmentId === assignmentId &&
        error instanceof WebApiError &&
        error.code === 'ASSIGNMENT_NOT_FOUND'
      ) {
        this.fail(error, '该作业已删除或不可访问。');
      }
    }
  }

  /** null means a fresh server snapshot confirms this question is already submitted. */
  async resumeAnswerSession(
    assignmentId: string,
    problemId: string,
  ): Promise<HomeworkAnswerSessionResponse | null> {
    const lifecycleEpoch = this.lifecycleEpoch;
    try {
      return await this.answerSession(assignmentId, problemId);
    } catch (error: unknown) {
      if (!(error instanceof WebApiError) || error.code !== 'ASSIGNMENT_NOT_OPEN') throw error;
      if (lifecycleEpoch !== this.lifecycleEpoch) throw error;
      try {
        const response = await this.identity.authorized(
          `/homework-assignments/${encodeURIComponent(assignmentId)}`,
          homeworkAssignmentResponseSchema,
        );
        if (lifecycleEpoch !== this.lifecycleEpoch) throw error;
        const assignment = response.data.assignment;
        if (
          latestHomeworkSubmissionForProblem(assignment, problemId) === null ||
          canAnswerHomeworkProblem(assignment, problemId)
        )
          throw error;
        this.clearAnswerDraft(assignmentId);
        if (this.stateValue.selectedAssignment?.assignmentId === assignmentId) {
          this.update({
            ...this.stateValue,
            selectedAssignment: assignment,
            assignments: this.stateValue.assignments.map((item) =>
              item.assignmentId === assignmentId ? assignment : item,
            ),
            pending: null,
            error: null,
            notice: '本题已提交，请继续下一题。',
          });
        }
        return null;
      } catch (refreshError: unknown) {
        if (
          lifecycleEpoch === this.lifecycleEpoch &&
          this.stateValue.selectedAssignment?.assignmentId === assignmentId
        )
          this.fail(refreshError, '计时同步失败，请重试。');
        throw refreshError;
      }
    }
  }

  async answerSession(
    assignmentId: string,
    problemId: string,
    draft?: HomeworkAnswerSessionBody['draft'],
  ): Promise<HomeworkAnswerSessionResponse> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('sync-answer-session');
    try {
      const response = await this.identity.authorized(
        `/homework-assignments/${encodeURIComponent(assignmentId)}/session`,
        homeworkAnswerSessionResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-answer-session'),
          body: { problemId, ...(draft === undefined ? {} : { draft }) },
        },
      );
      if (lifecycleEpoch === this.lifecycleEpoch)
        this.update({ ...this.stateValue, pending: null, error: null });
      return response;
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '服务端计时同步失败，请重试。');
      throw error;
    }
  }

  async previewAnswer(
    assignmentId: string,
    problemId: string,
    answer: readonly ProblemMoveIntent[],
  ): Promise<HomeworkAnswerPreviewResponse['data']> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('preview-answer');
    try {
      const response = await this.identity.authorized(
        `/homework-assignments/${encodeURIComponent(assignmentId)}/preview`,
        homeworkAnswerPreviewResponseSchema,
        { method: 'POST', body: { problemId, answer: [...answer] } },
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return response.data;
      this.update({
        ...this.stateValue,
        preview: response.data,
        pending: null,
        error: null,
      });
      return response.data;
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '该落子无法加入答案。');
      throw error;
    }
  }

  async submitAnswer(
    assignmentId: string,
    problemId: string,
    answer: readonly ProblemMoveIntent[],
    answerTrace: HomeworkAnswerTrace,
    elapsedMs: number,
    submissionReason?: 'TIMEOUT',
  ): Promise<SubmissionSnapshot> {
    const lifecycleEpoch = this.lifecycleEpoch;
    const body = {
      problemId,
      answer: [...answer],
      answerTrace,
      elapsedMs,
      ...(submissionReason === undefined ? {} : { submissionReason }),
    };
    const commandKey = `${assignmentId}:${JSON.stringify(body)}`;
    const requestId =
      this.submissionRequestIds.get(commandKey) ?? nextRequestId('web-homework-answer');
    this.submissionRequestIds.set(commandKey, requestId);
    this.pending('submit-answer');
    try {
      const submissionResponse = await this.identity.authorized(
        `/homework-assignments/${encodeURIComponent(assignmentId)}/submissions`,
        submissionResponseSchema,
        {
          method: 'POST',
          requestId,
          body,
        },
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return submissionResponse.data.submission;
      this.submissionRequestIds.delete(commandKey);
      this.clearAnswerDraft(assignmentId);
      // A confirmed submission must not turn into a failure because a subsequent list GET fails.
      this.applySubmission(submissionResponse.data.submission, 'answer');
      return submissionResponse.data.submission;
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '答案提交失败。');
      throw error;
    }
  }

  async reviewSubmission(
    submissionId: string,
    submissionVersion: number,
    status: Extract<SubmissionStatus, 'CORRECT' | 'INCORRECT'>,
    score: number,
    feedback: string,
  ): Promise<void> {
    const lifecycleEpoch = this.lifecycleEpoch;
    this.pending('review-submission');
    let reviewedSubmission: SubmissionSnapshot;
    try {
      const response = await this.identity.authorized(
        `/homework-submissions/${encodeURIComponent(submissionId)}/review`,
        submissionResponseSchema,
        {
          method: 'POST',
          requestId: nextRequestId('web-homework-review'),
          body: {
            expectedSubmissionVersion: submissionVersion,
            status,
            score,
            feedback: feedback.trim().length === 0 ? null : feedback.trim(),
          },
        },
      );
      reviewedSubmission = response.data.submission;
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch) this.fail(error, '批改保存失败。');
      throw error;
    }

    if (lifecycleEpoch !== this.lifecycleEpoch) return;
    this.applySubmission(reviewedSubmission, 'review');
  }

  async loadSubmissionReplay(
    submissionId: string,
  ): Promise<HomeworkSubmissionReplayResponse['data']> {
    const lifecycleEpoch = this.lifecycleEpoch;
    const requestEpoch = ++this.replayRequestEpoch;
    this.replayErrorMessage = null;
    this.pending('load-submission-replay');
    try {
      const response = await this.identity.authorized(
        `/homework-submissions/${encodeURIComponent(submissionId)}/replay`,
        homeworkSubmissionReplayResponseSchema,
      );
      if (lifecycleEpoch !== this.lifecycleEpoch || requestEpoch !== this.replayRequestEpoch) {
        return response.data;
      }
      this.replayErrorMessage = null;
      this.update({ ...this.stateValue, pending: null, error: null });
      return response.data;
    } catch (error: unknown) {
      if (lifecycleEpoch === this.lifecycleEpoch && requestEpoch === this.replayRequestEpoch) {
        const message = userErrorMessage(error, '学生答案棋谱加载失败。');
        this.replayErrorMessage = message;
        this.update({ ...this.stateValue, pending: null, error: message, notice: null });
      }
      throw error;
    }
  }

  cancelSubmissionReplay(): void {
    this.replayRequestEpoch += 1;
    const shouldClearPending = this.stateValue.pending === 'load-submission-replay';
    const shouldClearError =
      this.replayErrorMessage !== null && this.stateValue.error === this.replayErrorMessage;
    this.replayErrorMessage = null;
    if (shouldClearPending || shouldClearError) {
      this.update({
        ...this.stateValue,
        pending: shouldClearPending ? null : this.stateValue.pending,
        error: shouldClearError ? null : this.stateValue.error,
      });
    }
  }

  private async loadAllClassHomeworks(
    classId: string,
    lifecycleEpoch: number,
  ): Promise<readonly HomeworkSnapshot[]> {
    const homeworks: HomeworkSnapshot[] = [];
    const seenCursors = new Set<string>();
    let after: string | null = null;
    for (let page = 0; page < MAX_HOMEWORK_PAGES; page += 1) {
      if (lifecycleEpoch !== this.lifecycleEpoch) return homeworks;
      const query = new URLSearchParams({ limit: '50' });
      if (after !== null) query.set('after', after);
      const response = await this.identity.authorized(
        `/classes/${encodeURIComponent(classId)}/homeworks?${query.toString()}`,
        homeworksResponseSchema,
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return homeworks;
      homeworks.push(...response.data.homeworks);
      const nextAfter = response.data.nextAfter;
      if (nextAfter === null) return homeworks;
      if (seenCursors.has(nextAfter)) throw new Error('作业分页游标未推进。');
      seenCursors.add(nextAfter);
      after = nextAfter;
    }
    throw new Error('作业数据页数超过安全上限。');
  }

  private async loadAllAssignmentsForHomework(
    homeworkId: string,
    lifecycleEpoch: number,
  ): Promise<readonly HomeworkAssignmentSnapshot[]> {
    const assignments: HomeworkAssignmentSnapshot[] = [];
    const seenCursors = new Set<string>();
    let after: string | null = null;
    for (let page = 0; page < MAX_HOMEWORK_PAGES; page += 1) {
      if (lifecycleEpoch !== this.lifecycleEpoch) return assignments;
      const query = new URLSearchParams({ limit: '50' });
      if (after !== null) query.set('after', after);
      const response = await this.identity.authorized(
        `/homeworks/${encodeURIComponent(homeworkId)}/assignments?${query.toString()}`,
        assignmentsResponseSchema,
      );
      if (lifecycleEpoch !== this.lifecycleEpoch) return assignments;
      assignments.push(...response.data.assignments);
      const nextAfter = response.data.nextAfter;
      if (nextAfter === null) return assignments;
      if (seenCursors.has(nextAfter)) throw new Error('学生作答分页游标未推进。');
      seenCursors.add(nextAfter);
      after = nextAfter;
    }
    throw new Error('学生作答数据页数超过安全上限。');
  }

  private applySubmission(submission: SubmissionSnapshot, mode: 'answer' | 'review'): void {
    const replaceSubmission = (
      assignments: readonly HomeworkAssignmentSnapshot[],
    ): readonly HomeworkAssignmentSnapshot[] =>
      assignments.map((assignment) => {
        if (assignment.assignmentId !== submission.assignmentId) return assignment;
        const hasSubmission = assignment.submissions.some(
          (candidate) => candidate.submissionId === submission.submissionId,
        );
        const updatedAssignment: HomeworkAssignmentSnapshot = {
          ...assignment,
          submissions: hasSubmission
            ? assignment.submissions.map((candidate) =>
                candidate.submissionId === submission.submissionId ? submission : candidate,
              )
            : [...assignment.submissions, submission],
        };
        const fullyReviewed =
          updatedAssignment.items.length > 0 &&
          updatedAssignment.items.every((item) => {
            const latest = latestHomeworkSubmissionForProblem(
              updatedAssignment,
              item.problem.problemId,
            );
            return latest?.status === 'CORRECT' || latest?.status === 'INCORRECT';
          });
        const allAnswered = updatedAssignment.items.every(
          (item) =>
            latestHomeworkSubmissionForProblem(updatedAssignment, item.problem.problemId) !== null,
        );
        return {
          ...updatedAssignment,
          status: fullyReviewed
            ? ('REVIEWED' as const)
            : allAnswered
              ? ('SUBMITTED' as const)
              : ('IN_PROGRESS' as const),
          submittedAt:
            mode === 'review'
              ? assignment.submittedAt
              : allAnswered
                ? submission.submittedAt
                : null,
          reviewedAt: fullyReviewed
            ? mode === 'review'
              ? submission.reviewedAt
              : submission.submittedAt
            : null,
        };
      });

    this.update({
      ...this.stateValue,
      assignments: replaceSubmission(this.stateValue.assignments),
      reviewAssignmentsByHomework: Object.fromEntries(
        Object.entries(this.stateValue.reviewAssignmentsByHomework).map(
          ([homeworkId, assignments]) => [homeworkId, replaceSubmission(assignments)],
        ),
      ),
      selectedAssignment:
        this.stateValue.selectedAssignment === null
          ? null
          : (replaceSubmission([this.stateValue.selectedAssignment])[0] ?? null),
      preview: mode === 'answer' ? null : this.stateValue.preview,
      pending: null,
      error: null,
    });
  }

  private pending(pending: string): void {
    this.researchErrorMessage = null;
    this.replayErrorMessage = null;
    this.update({ ...this.stateValue, pending, error: null, notice: null });
  }

  private studentLearningScope(): string | null {
    const session = this.identity.snapshot().session;
    const membership = session?.data.memberships.find(
      (candidate) => candidate.membershipId === session.data.tokens.selectedMembershipId,
    );
    if (session === null || membership?.role !== 'STUDENT') return null;
    return `${session.data.user.userId}:${membership.organization.organizationId}:${membership.membershipId}`;
  }

  private updateStudentLearning(summary: StudentHomeLearningSummary): void {
    this.studentLearningValue = summary;
    this.listeners.forEach((listener) => listener());
  }

  private fail(error: unknown, fallback: string): void {
    this.researchErrorMessage = null;
    this.replayErrorMessage = null;
    if (error instanceof WebApiError && error.code === 'ASSIGNMENT_NOT_FOUND') {
      const assignmentId = this.stateValue.selectedAssignment?.assignmentId;
      if (assignmentId) this.clearAnswerDraft(assignmentId);
      this.update({
        ...this.stateValue,
        selectedAssignment: null,
        preview: null,
        assignments: this.stateValue.assignments.filter(
          (item) => item.assignmentId !== assignmentId,
        ),
        pending: null,
        error: '该作业已删除或不可访问，请返回作业列表。',
        notice: null,
      });
      return;
    }
    this.update({
      ...this.stateValue,
      pending: null,
      error: userErrorMessage(error, fallback),
      notice: null,
    });
  }

  private update(state: HomeworkState): void {
    if (state.notice !== this.stateValue.notice && this.publishNoticeTimer !== null) {
      clearTimeout(this.publishNoticeTimer);
      this.publishNoticeTimer = null;
    }
    this.stateValue = state;
    this.listeners.forEach((listener) => listener());
  }
}
